import { PlusOutlined } from "@ant-design/icons"
import type { UploadFile } from "antd"
import { Button, Card, Empty, Flex, Skeleton, Typography, Upload } from "antd"
import React, { Suspense, lazy, useState } from "react"
import { Link } from "react-router-dom"

import { RoutesPaths } from "../../../../../../../constants/routes.ts"
import { AlbumFilesSettings } from "../../../../constants/album-files-settings.tsx"
import { FileDragOverlay } from "../../../FileDragOverlay.tsx"
import { RecentFileCard } from "./RecentFileCard.tsx"

const FilePreviewModal = lazy(
  () =>
    import("../../../../../../../components/FilePreviewModal/FilePreviewModal")
)

const { Title } = Typography

interface ProjectRecentFilesProps {
  projectId: string
  fileList: UploadFile[]
  uploadAllowed: boolean
  loading: boolean
  maxFileSize: number
  allowedExtensions: string[]
  recentFilesLimit: number
  onBeforeUpload: (file: File) => boolean
  // onCancelUpload: (uid: string) => void
}

export function ProjectRecentFiles({
  projectId,
  fileList,
  uploadAllowed,
  loading,
  maxFileSize,
  allowedExtensions,
  recentFilesLimit,
  onBeforeUpload
  // onCancelUpload
}: ProjectRecentFilesProps) {
  const [previewFile, setPreviewFile] = useState<UploadFile | null>(null)

  const uploadProps = {
    showUploadList: false,
    multiple: true,
    accept: allowedExtensions.map((ext) => `.${ext}`).join(","),
    beforeUpload: onBeforeUpload
  }

  const displayFiles = fileList.slice(0, recentFilesLimit)
  const hasUploadingFiles = fileList.some((f) => f.status === "uploading")

  return (
    <div className="border-t pt-2 relative border-border">
      {previewFile?.url && (
        <Suspense
          fallback={
            <Skeleton.Node
              active
              className={"fixed z-10 m-auto inset-0 h-[50vh] w-[50vw]"}
            />
          }
        >
          <FilePreviewModal
            visible
            onClose={() => setPreviewFile(null)}
            doc={{
              file: previewFile?.url,
              file_name: previewFile?.fileName,
              name: previewFile?.name
            }}
          />
        </Suspense>
      )}

      <Flex justify="space-between" align="center" className="mb-2">
        <Title level={4} className="!mb-0">
          Recent Files
        </Title>
        <Link to={RoutesPaths.project.view(projectId).allFiles}>
          <Button type={"primary"}>View All</Button>
        </Link>
      </Flex>

      <FileDragOverlay
        message="Drag and drop files here"
        acceptedExtensions={AlbumFilesSettings.ALLOWED_FILE_EXTENSIONS}
        maxFileSize={AlbumFilesSettings.MAX_SIZE_MB}
        onFiles={(files) => files.forEach(onBeforeUpload)}
      />

      <div className="flex gap-4 overflow-x-auto tailwind-scroll items-stretch pb-2">
        {loading ? (
          Array.from({ length: recentFilesLimit }).map((_, i) => (
            <Skeleton.Node key={i} active className="shrink-0 size-36 " />
          ))
        ) : (
          <>
            {fileList.length === 0 ? (
              <>
                {uploadAllowed && (
                  <Upload
                    {...uploadProps}
                    className="[&_.ant-upload]:size-full"
                  >
                    <Button className="size-full" icon={<PlusOutlined />}>
                      Upload File
                    </Button>
                  </Upload>
                )}
                <Empty
                  description="No files yet"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              </>
            ) : (
              <>
                {uploadAllowed && (
                  <Upload
                    {...uploadProps}
                    rootClassName="h-full [&_.ant-upload]:w-36"
                  >
                    <Card
                      hoverable
                      className="size-36  border-dashed cursor-pointer"
                      styles={{ body: { padding: 8, height: "100%" } }}
                    >
                      <Flex
                        vertical
                        align="center"
                        justify="center"
                        gap={8}
                        className="h-full text-gray-400"
                      >
                        {hasUploadingFiles ? (
                          <Skeleton.Avatar active size="large" />
                        ) : (
                          <>
                            <PlusOutlined className="text-2xl" />
                            <Typography.Text
                              type="secondary"
                              className="text-xs"
                            >
                              Upload
                            </Typography.Text>
                          </>
                        )}
                      </Flex>
                    </Card>
                  </Upload>
                )}

                {displayFiles.map((file) => (
                  <div key={file.uid} className="shrink-0 w-36">
                    <RecentFileCard
                      file={file}
                      // onCancelUpload={onCancelUpload}
                      onPreviewFile={setPreviewFile}
                    />
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}
